// The DAY divider: a dated hairline between two consecutive transcript rows that fall on different
// local calendar days.
//
// Why it exists: every bubble shows a CLOCK time and no date (`formatMessageTime`), so a thread that
// spans midnight reads as going backwards — "8:32 PM" followed by "8:26 PM", which is the next day's
// message. The clock alone cannot say that, and the reader is left to guess.
//
// Anchors are computed over the rows the transcript will ACTUALLY draw (the `isVisibleMessage`-filtered
// list), never the raw list: a divider anchored to a row that is filtered out renders orphaned above the
// wrong message. The first row never anchors one — a divider marks a CHANGE of day, so a single-day
// conversation carries no chrome at all.
//
// Shared 1:1 with iOS `ChatDayDividers` (`Components/ChatDayDivider.swift`) and Android
// `features/chat/ChatDayDividers.kt`. The three must agree on the rules below, so change them together.

// Which separator a row draws. The point of the type is that it has no "both" case: a row is at most
// ONE hairline, so "we showed the unread divider under the day divider" is not a state the renderers can
// reach by disagreeing with each other. Shared 1:1 with iOS `ChatDayDividers.Kind` and Android
// `ChatDayDividers.Kind`.
//
//  - 'day'            a new day, and the reader has read everything above it
//  - 'unread'         the first unread row, mid-day — no date to add
//  - 'dayAndUnread'   both at once: ONE hairline reading "<day> · New messages" (`combinedDividerLabel`),
//                     the common case on an overnight return
export type DividerKind = 'none' | 'day' | 'unread' | 'dayAndUnread';

export function dividerKind(isDayAnchor: boolean, isUnread: boolean): DividerKind {
  if (isDayAnchor && isUnread) return 'dayAndUnread';
  if (isDayAnchor) return 'day';
  if (isUnread) return 'unread';
  return 'none';
}

type DayDividerMessage = {
  id?: string;
  createdDate?: string;
};

// A local calendar day as `YYYY-MM-DD` in the VIEWER's time zone. Deliberately not an epoch-day number:
// local midnight is what the reader means by "a different day", and day length is not constant (DST).
export function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// The rows that OPEN a new local day, mapped to a Date inside that day (local noon, so the label's own
// formatting can never tip into an adjacent day through a zone or DST edge).
//
// `messages` must be in CHRONOLOGICAL order and already filtered to what will be drawn.
//
// ⚠️ A row with no parseable `createdDate` counts as **now** (`now` is injectable so a test can pin it).
// That matches iOS, where `ChatMessage.createdAt` is non-null and defaults to the current date, and it is
// a deliberate cross-surface rule rather than an accident of either model: an optimistic send is dateless
// HERE and dated THERE, so without the fallback the two transcripts would draw different dividers for the
// same thread. A local bubble IS being created now, so "now" is also the true answer.
export function computeDayDividers(
  messages: DayDividerMessage[],
  now: Date = new Date(),
): Map<string, Date> {
  const anchors = new Map<string, Date>();
  let previousKey: string | null = null;
  for (const m of messages) {
    const parsed = m.createdDate ? new Date(m.createdDate) : null;
    const when = parsed && !Number.isNaN(parsed.getTime()) ? parsed : now;
    const key = localDayKey(when);
    if (previousKey !== null && key !== previousKey && m.id) {
      anchors.set(m.id, new Date(when.getFullYear(), when.getMonth(), when.getDate(), 12));
    }
    previousKey = key;
  }
  return anchors;
}

export type DayDividerLabels = {
  today: string;
  yesterday: string;
};

// The label for a row that opens a new day AND is the first unread one — which is the COMMON case, not
// an edge: you leave the chat, messages arrive overnight, so the first unread row IS the first row of
// the new day. Stacking two hairlines there is noise, and dropping either one loses a fact the reader
// wants on exactly the return where they want it most — so the two fold into ONE divider carrying both
// words.
//
// Composed from the two EXISTING strings rather than a new "New messages on {date}" key: nothing to
// translate into 37 bundles twice over, and ` · ` is already how this app joins two settled labels.
export function combinedDividerLabel(day: string, unread: string): string {
  return [day, unread].filter(Boolean).join(' · ');
}

// The divider's words for `day`: the app's own "Today"/"Yesterday" when it is one of those, else the
// abbreviated weekday + date ("Mon, Jul 6"), carrying the YEAR when the day is not in the current one
// ("Mon, Jul 6, 2025") — without it a divider a year back is indistinguishable from this year's.
//
// The relative words are the activity log's existing keys (`agents.activity.today` / `.yesterday`), not
// new chat-scoped ones: a transcript and the activity list one screen over must agree on what
// "Yesterday" means. The natives make the same call against their own copies of those keys.
//
// `locale` is the ACTIVE i18n language, not the browser default. Passing `undefined` to
// `toLocaleDateString` formats with the browser's locale, so a reader who picked a language in Settings
// while their browser stays in another one would get "Yesterday" from the chosen language and
// "Mon, Jul 6" from the browser's — one divider, two languages. Callers pass
// `i18n.language || undefined`, the same way `timelineDayLabel` and `scheduledDateLabel` already do.
export function dayDividerLabel(
  day: Date,
  labels: DayDividerLabels,
  now: Date = new Date(),
  locale?: string,
): string {
  const key = localDayKey(day);
  if (key === localDayKey(now)) return labels.today;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (key === localDayKey(yesterday)) return labels.yesterday;
  const sameYear = day.getFullYear() === now.getFullYear();
  try {
    return day.toLocaleDateString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  } catch {
    // Intl unavailable/misconfigured — the key is still a readable, unambiguous date.
    return key;
  }
}
